<template>
  <tab-layout :full-width="true">
  <div slot="tabContent" class="wrap-drafts dashboard committee_nomination_dashboard bg-white rounded mb-4 p-3">
    <page-heading>Nomination Dashboard</page-heading>
    <div class="alert alert-info">
      For your nominations to open at your selected time, you must contact the Campdraft One Team to review, approve,
      and publish your Campdraft One setup. To check that your nominations will open at your selected time, you may
      refer to your Campdraft One setup approval email, or navigate to "Nominations" where your draft should appear
      with the nomination opening date and time listed.
    </div>

    <div class="d-flex">
      <div class="historical_filter">
        <f-search-select
          id="historical_year"
          label="Filter by year"
          :value="params.season"
          :options="years.map(y => ({ id: y, name: String(y) }))"
          :searchable="false"
          @input="setYear(Number($event))"
        />
      </div>
    </div>

    <div class="drafts-filter display-md">
      <label>Sort drafts by:</label>
      <div class="drafts-filter__wrap">
        <div class="display-mob sortable position-relative">
          <button class="btn btn-link p-0" @click="setSort('noms_open')">
            Noms Open
            <span class="sort-arrows">
              <span :class="['sort-arrow sort-arrow--up', { active: params.sort === 'noms_open' && params.direction === 'asc' }]" />
              <span :class="['sort-arrow sort-arrow--down', { active: params.sort === 'noms_open' && params.direction === 'desc' }]" />
            </span>
          </button>
        </div>
        <div class="display-mob sortable position-relative">
          <button class="btn btn-link p-0" @click="setSort('start_date')">
            Start Date
            <span class="sort-arrows">
              <span :class="['sort-arrow sort-arrow--up', { active: params.sort === 'start_date' && params.direction === 'asc' }]" />
              <span :class="['sort-arrow sort-arrow--down', { active: params.sort === 'start_date' && params.direction === 'desc' }]" />
            </span>
          </button>
        </div>
        <div class="display-mob sortable position-relative">
          <button class="btn btn-link p-0" @click="setSort('end_date')">
            End Date
            <span class="sort-arrows">
              <span :class="['sort-arrow sort-arrow--up', { active: params.sort === 'end_date' && params.direction === 'asc' }]" />
              <span :class="['sort-arrow sort-arrow--down', { active: params.sort === 'end_date' && params.direction === 'desc' }]" />
            </span>
          </button>
        </div>
        <div class="sortable position-relative">
          <button class="btn btn-link p-0" @click="setSort('draft_state_id')">
            Status
            <span class="sort-arrows">
              <span :class="['sort-arrow sort-arrow--up', { active: params.sort === 'draft_state_id' && params.direction === 'asc' }]" />
              <span :class="['sort-arrow sort-arrow--down', { active: params.sort === 'draft_state_id' && params.direction === 'desc' }]" />
            </span>
          </button>
        </div>
      </div>
    </div>

    <grid-table
      :columns="columns"
      :rows="drafts"
      :loading="loading"
      :sort="params.sort"
      :direction="params.direction"
      :open-id="visibleOpenDraftId"
      empty-text="No drafts found for this year."
      class="mt-2"
      @sort="setSort"
    >
      <template #cell:name="{ row }">
        <router-link
          :to="{ name: 'Committees Dashboard', params: { draftId: String(row.id) }, query: nominationFilters.list ? { list: nominationFilters.list } : {} }"
          class="btn btn-link text-wrap text-left p-0 text-break"
        >
          {{ row.name }}
        </router-link>
      </template>

      <template #cell:noms_open="{ row }">{{ formatDateTime(row.noms_open) }}&nbsp;</template>
      <template #cell:start_date="{ row }">{{ formatDate(row.start_date) }}&nbsp;</template>
      <template #cell:end_date="{ row }">{{ formatDate(row.end_date) }}&nbsp;</template>

      <template #cell:status="{ row }">
        <span class="badge alert-success">{{ row.state ? row.state.name : '' }}</span>
      </template>

      <template #cell:actions="{ row }">
        <div class="actions__wrap">
          <app-tooltip text="Add/Edit Events, Add/Edit Event Restrictions">
            <router-link :to="{ name: 'Draft View', params: { id: row.id } }" class="icon-btn icon-btn--view" />
          </app-tooltip>
          <app-tooltip text="Edit Draft Details, Change Draft Status" align="right">
            <router-link :to="{ name: 'Draft Edit', params: { id: row.id } }" class="icon-btn icon-btn--edit" />
          </app-tooltip>
        </div>
      </template>

      <template #cell:events="{ row }">
        <!-- The open draft's toggle only collapses/expands the events subtable locally,
             without a router navigation, so the URL keeps pointing at the draft. -->
        <a
          v-if="openDraftId === row.id"
          href="#"
          class="icon-btn"
          :class="eventsCollapsed ? 'icon-btn--events' : 'icon-btn--events--open'"
          title="Toggle events"
          @click.prevent="eventsCollapsed = !eventsCollapsed"
        />
        <router-link
          v-else
          :to="{ name: 'Committees Dashboard', params: { draftId: String(row.id) }, query: nominationFilters.list ? { list: nominationFilters.list } : {} }"
          class="icon-btn icon-btn--events"
          title="Toggle events"
        />
      </template>

      <template #expand="{ row }">
        <form-alert v-if="subAlert.eventId == null" :message="subAlert.message" :type="subAlert.type" @dismiss="clearSubAlert" />
        <committee-draft-events-table
          :events="events"
          :totals="totals"
          :loading-events="loadingEvents"
          :reordering="reorderingEvents"
          :deleting="deleting"
          :draft-id="row.id"
          :draft-state-id="row.state ? row.state.id : null"
          :active-event-id="nominationFilters.event_id"
          :list="nominationFilters.list"
          :alert="subAlert"
          @dismiss-alert="clearSubAlert"
          @lock="onLock"
          @drag-end="onDragEnd"
          @confirm-delete="confirmDelete"
          @confirm-split="confirmSplit"
        />
      </template>
    </grid-table>

    <committee-dashboard-nominations
      v-if="openDraftId != null"
      :draft-id="openDraftId"
      :nominations="nominations"
      :loading-nominations="loadingNominations"
      :list-tab-items="listTabItems"
      :active-list="nominationFilters.list"
      :search-input="nominationSearchInput"
      :event="nominationEvent"
      :events="events"
      :event-members-allowed="openDraftEventMembersAllowed"
      :draft-state-id="openDraftStateId"
      @list-change="onListChange"
      @search-input="onSearchInput"
      @reload="onNominationsReload"
    />

    <confirm-modal
      v-if="eventToDelete"
      :message="`You are about to delete event ${eventToDelete.name}. Are you sure?`"
      :loading="deleting"
      @confirm="handleDelete"
      @cancel="eventToDelete = null"
    />

    <confirm-modal
      v-if="eventToSplit != null"
      message="Are you sure you want to split this event into A and B?"
      :loading="splitting"
      @confirm="handleSplit"
      @cancel="eventToSplit = null"
    >
      <div slot="extra" class="alert alert-info alert-simple text-center mt-3 mb-0">
        Splitting an event works differently depending on its settings: a regular, a combined
        and a conjunction event are split in different ways.
        <button type="button" class="btn btn-link p-0 align-baseline" @click="showSplitInfo = true">How does split work?</button>
      </div>
    </confirm-modal>

    <doc-modal v-if="showSplitInfo" doc="events.split" @close="showSplitInfo = false" />

  </div>
  </tab-layout>
</template>

<script>
import { defineComponent, onMounted, onBeforeUnmount, watch, getCurrentInstance, ref, reactive, computed } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import ConfirmModal from '@/components/ConfirmModal'
import DocModal from '@/components/Docs/DocModal'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import GridTable from '@/components/GridTable'
import AppTooltip from '@/components/AppTooltip'
import useCommitteeDrafts from '@/composables/committees/useCommitteeDrafts'
import useDraftEventsDashboard from '@/composables/drafts/useDraftEventsDashboard'
import useDraftEvents from '@/composables/drafts/useDraftEvents'
import getYears from '@/composables/useYears'
import nominationsApi from '@/services/api/nominations'
import eventsApi from '@/services/api/events'
import useNominationLists from '@/composables/nominations/useNominationLists'
import { formatDate, formatDateTime } from '@/utils/date'
import FormAlert from '@/components/FormAlert'
import CommitteeDraftEventsTable from './components/CommitteeDraftEventsTable'
import CommitteeDashboardNominations from './components/CommitteeDashboardNominations'

const COLUMNS = [
  { key: 'name', label: 'Name', width: '1fr', sortable: true },
  { key: 'noms_open', label: 'Noms Open', width: '1fr', sortable: true, mobile: 'label' },
  { key: 'start_date', label: 'Start Date', width: '1fr', sortable: true, mobile: 'label' },
  { key: 'end_date', label: 'End Date', width: '1fr', sortable: true, mobile: 'label' },
  { key: 'status', label: 'Status', width: '1fr', mobile: 'label', tablet: 'wrap', tabletWidth: '50%' },
  { key: 'actions', label: 'Actions', width: '0.6fr', tablet: 'wrap', tabletWidth: '25%' },
  { key: 'events', label: 'Events', width: '0.4fr', cellClass: 'text-center', tablet: 'wrap', tabletWidth: '25%' }
]

export default defineComponent({
  name: 'CommitteeDashboard',
  components: { TabLayout, PageHeading, FSearchSelect, ConfirmModal, DocModal, FormAlert, GridTable, AppTooltip, CommitteeDraftEventsTable, CommitteeDashboardNominations },
  props: {
    draftId: { type: String, default: null }
  },
  setup(props) {
    const { proxy } = getCurrentInstance()
    // The result of an action on an event is shown right above that event's row (eventId) instead of
    // at the top of the events table, which is far away when the draft has many events. Actions that
    // are not tied to one event (reorder, loading) keep the notice above the table (eventId null).
    const alertEventId = ref(null)
    let subAlertSeq = 0
    const subAlert = ref({ message: null, type: 'success', eventId: null, id: 0 })
    // Disappears on its own after 5s, like the page-top alert (AppAlert) it replaces.
    let subAlertTimer = null
    const showSubAlert = (message, type) => {
      clearTimeout(subAlertTimer)
      subAlert.value = { message, type, eventId: alertEventId.value, id: ++subAlertSeq }
      subAlertTimer = setTimeout(() => { subAlert.value = { message: null, type: 'success', eventId: null } }, 5000)
    }
    const clearSubAlert = () => {
      clearTimeout(subAlertTimer)
      subAlert.value = { message: null, type: 'success', eventId: null, id: 0 }
    }
    const setSubError = msg => showSubAlert(msg, 'warning')
    const setSubSuccess = msg => showSubAlert(msg, 'success')

    onBeforeUnmount(() => clearTimeout(subAlertTimer))

    const { drafts, loading, params, fetch, setYear, setSort } = useCommitteeDrafts()
    const { openDraftId, events, totals, loadingEvents, toggle, reload, lockEvent } = useDraftEventsDashboard({ onError: setSubError, onSuccess: setSubSuccess })
    const { deleting, deleteEvent, reorderEvents } = useDraftEvents({ onError: setSubError, onSuccess: setSubSuccess })
    const years = getYears()
    const { listItems: listTabItems, defaultList } = useNominationLists()

    const eventToDelete = ref(null)
    const eventToSplit = ref(null)
    const showSplitInfo = ref(false)
    const splitting = ref(false)
    const reorderingEvents = ref(false)

    const nominations = ref([])
    const loadingNominations = ref(false)
    const nominationFilters = reactive({ event_id: null, list: defaultList.value, search: '' })
    const nominationEvent = computed(() => {
      if (nominationFilters.event_id == null) return null
      return events.value.find(e => e.id === nominationFilters.event_id) || null
    })
    const nominationSearchInput = ref('')
    // Local-only collapse of the open draft's events subtable — toggled by the +/- icon
    // without touching the URL, and reset so the subtable is expanded on each navigation.
    const eventsCollapsed = ref(false)
    const visibleOpenDraftId = computed(() => (eventsCollapsed.value ? null : openDraftId.value))
    const openDraftEventMembersAllowed = computed(() => {
      if (openDraftId.value == null) return false
      const draft = drafts.value.find(d => d.id === openDraftId.value)
      return draft != null ? !!draft.event_members_allowed : false
    })
    const openDraftStateId = computed(() => {
      if (openDraftId.value == null) return null
      const draft = drafts.value.find(d => d.id === openDraftId.value)
      return draft != null && draft.state != null ? draft.state.id : null
    })
    let nominationSearchTimer = null

    async function fetchNominations() {
      if (openDraftId.value == null) return
      loadingNominations.value = true
      try {
        const query = {}
        if (nominationFilters.event_id != null) query.event_id = nominationFilters.event_id
        if (nominationFilters.list) query.list = nominationFilters.list
        if (nominationFilters.search) query.search = nominationFilters.search
        const res = await nominationsApi.getDashboard(String(openDraftId.value), query)
        const data = res.data.data
        nominations.value = data.nominations != null ? data.nominations : data
      } catch (_) {
        nominations.value = []
      } finally {
        loadingNominations.value = false
      }
    }

    async function moveGroup(group, list) {
      const ids = group.nominations.map(n => n.id)
      await nominationsApi.bulkMove({ nomination_ids: ids, list })
      await fetchNominations()
    }

    // Nomination list moves change per-event counts in the events subtable,
    // so reload events together with the nominations list.
    function onNominationsReload() {
      fetchNominations()
      reload()
    }

    async function handleRouteChange(route) {
      const draftId = route.params.draftId
      if (draftId == null || draftId === '') {
        openDraftId.value = null
        events.value = []
        nominations.value = []
        nominationFilters.event_id = null
        nominationFilters.list = defaultList.value
        nominationFilters.search = ''
        nominationSearchInput.value = ''
        return
      }
      const numId = Number(draftId)
      if (openDraftId.value !== numId) {
        eventsCollapsed.value = false
        await toggle(numId)
      }
      const q = route.query
      nominationFilters.event_id = q.event_id != null ? Number(q.event_id) : null
      nominationFilters.list = q.list != null ? q.list : defaultList.value
      nominationFilters.search = q.search != null ? q.search : ''
      nominationSearchInput.value = nominationFilters.search
      fetchNominations()
    }

    function onListChange(list) {
      nominationFilters.list = list
      proxy.$router.replace({
        name: proxy.$route.name,
        params: proxy.$route.params,
        query: { ...proxy.$route.query, list }
      }).catch(() => {})
    }

    function onSearchInput(val) {
      nominationSearchInput.value = val
      clearTimeout(nominationSearchTimer)
      nominationSearchTimer = setTimeout(() => {
        const query = { ...proxy.$route.query }
        if (val) query.search = val
        else delete query.search
        proxy.$router.replace({
          name: proxy.$route.name,
          params: proxy.$route.params,
          query
        }).catch(() => {})
      }, 300)
    }

    function confirmDelete(event) {
      eventToDelete.value = event
    }

    function onLock(eventId, isLocked) {
      alertEventId.value = eventId
      return lockEvent(eventId, isLocked)
    }

    // The deleted row is gone by the time the result is shown, so the notice goes above the event
    // that followed it (or the one before it when it was the last).
    function neighbourEventId(eventId) {
      const list = events.value
      const index = list.findIndex(e => e.id === eventId)
      if (index === -1) return null
      const next = list.slice(index + 1).find(e => e.parent_id == null || e.parent_id !== list[index].id)
      const previous = list.slice(0, index).reverse().find(e => e.id !== list[index].parent_id)
      return (next || previous || {}).id != null ? (next || previous).id : null
    }

    function confirmSplit(event) {
      eventToSplit.value = event
    }

    async function handleSplit() {
      if (eventToSplit.value == null) return
      splitting.value = true
      alertEventId.value = eventToSplit.value.id
      clearTimeout(subAlertTimer)
      subAlert.value = { message: null, type: 'success', eventId: null }
      try {
        const res = await eventsApi.split(eventToSplit.value.id)
        eventToSplit.value = null
        await reload()
        setSubSuccess(res.data.message || 'Event split successfully.')
      } catch (err) {
        const msg = err.response != null && err.response.data != null ? err.response.data.message : 'Failed to split event.'
        eventToSplit.value = null
        setSubError(msg)
      } finally {
        splitting.value = false
      }
    }

    async function handleDelete() {
      if (!eventToDelete.value) return
      const id = eventToDelete.value.id
      eventToDelete.value = null
      alertEventId.value = neighbourEventId(id)
      await deleteEvent(id)
      await reload()
    }

    async function onDragEnd(orderedIds, movedId = null) {
      if (reorderingEvents.value) return
      alertEventId.value = movedId

      const previousEvents = [...events.value]
      const eventsById = new Map(events.value.map(event => [event.id, event]))
      const reorderedEvents = orderedIds.map(id => eventsById.get(id)).filter(Boolean)

      if (reorderedEvents.length !== events.value.length) {
        await reload()
        return
      }

      events.value = reorderedEvents
      reorderingEvents.value = true

      try {
        const saved = await reorderEvents(orderedIds, openDraftId.value)
        if (!saved) {
          events.value = previousEvents
          await reload()
        }
      } finally {
        reorderingEvents.value = false
      }
    }

    function redirectToDefaultDraft() {
      if (drafts.value.length === 0) return
      const now = new Date()
      const activeDrafts = drafts.value.filter(
        draft => new Date(draft.start_date) <= now && now <= new Date(draft.end_date)
      )
      const candidates = activeDrafts.length > 0 ? activeDrafts : drafts.value
      const targetDraft = candidates.reduce((newest, draft) => (
        new Date(draft.start_date) > new Date(newest.start_date) ? draft : newest
      ))
      proxy.$router.replace({
        name: 'Committees Dashboard',
        params: { draftId: String(targetDraft.id) },
        query: proxy.$route.query
      }).catch(() => {})
    }

    watch(() => proxy.$route.fullPath, () => {
      if (proxy.$route.params.draftId == null || proxy.$route.params.draftId === '') {
        redirectToDefaultDraft()
        return
      }
      handleRouteChange(proxy.$route)
    })

    onMounted(async () => {
      await fetch()
      if (props.draftId) {
        await handleRouteChange(proxy.$route)
        return
      }
      redirectToDefaultDraft()
    })

    return {
      columns: COLUMNS,
      drafts,
      loading,
      params,
      years,
      setYear,
      setSort,
      openDraftId,
      visibleOpenDraftId,
      eventsCollapsed,
      events,
      totals,
      loadingEvents,
      lockEvent,
      eventToDelete,
      deleting,
      confirmDelete,
      handleDelete,
      subAlert,
      clearSubAlert,
      onLock,
      eventToSplit,
      showSplitInfo,
      splitting,
      confirmSplit,
      handleSplit,
      onDragEnd,
      reorderingEvents,
      formatDate,
      formatDateTime,
      nominations,
      loadingNominations,
      nominationEvent,
      nominationFilters,
      nominationSearchInput,
      listTabItems,
      onListChange,
      onSearchInput,
      moveGroup,
      fetchNominations,
      onNominationsReload,
      openDraftEventMembersAllowed,
      openDraftStateId
    }
  }
})
</script>

<style scoped>
.historical_filter ::v-deep label {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
}

.drafts-filter {
  margin-bottom: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/views/Committees/Dashboard/CommitteeDashboard.vue